/**
 * Query Parameter Storage Utility
 *
 * Manages localStorage for query parameters (practiceId, appointmentId, qrMode).
 * These params are:
 * 1. Read from URL query params
 * 2. Merged into kiosk_settings_machineContext in localStorage
 * 3. Override machine context values
 * 4. Removed from URL after being consumed (dissolved)
 *
 * Storage Format:
 * - Key: `kiosk_settings_machineContext`
 * - Value: JSON object with { orgId, practiceId, orgName, qrMode, appointmentId }
 *
 * Features:
 * - Persist indefinitely (same storage as SettingsTab)
 * - Override machine context on every load
 * - Merged with existing settings (not replaced)
 */

const SETTINGS_KEY = "kiosk_settings_machineContext";

export interface KioskMachineContext {
  orgId?: number;
  practiceId?: string;
  appointmentId?: string;
  orgName?: string;
  qrMode?: boolean;
  ehrLocationId?: string;
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get current machine context from localStorage
 *
 * @returns Current machine context or empty object
 */
function getMachineContext(): KioskMachineContext {
  if (!isStorageAvailable()) {
    return {};
  }

  try {
    const json = localStorage.getItem(SETTINGS_KEY);
    if (!json) return {};

    const context = JSON.parse(json) as KioskMachineContext;
    return context;
  } catch (error) {
    console.error("[ParamStorage] Failed to load machine context:", error);
    return {};
  }
}

/**
 * Save machine context to localStorage
 *
 * @param context - Machine context to save
 */
function saveMachineContext(context: KioskMachineContext): void {
  if (!isStorageAvailable()) {
    console.warn(
      "[ParamStorage] localStorage unavailable, context not persisted",
    );
    return;
  }

  try {
    const json = JSON.stringify(context);
    localStorage.setItem(SETTINGS_KEY, json);
    console.log("[ParamStorage] Saved machine context:", context);
  } catch (error) {
    console.error("[ParamStorage] Failed to save machine context:", error);
  }
}

/**
 * Merge URL params into existing machine context and save
 * URL params override existing values
 *
 * Only defined values are merged. A launch that supplies just one corrective
 * param (e.g. `?ehrLocationId=` with no practiceId) passes `undefined` for the
 * companion fields; those must be treated as "absent", not as an instruction to
 * clear the cache. A plain shallow spread would write `practiceId: undefined`
 * over the cached value and — because `JSON.stringify` drops undefined keys —
 * erase the cached practiceId on disk, leaving the next bare launch
 * unconfigured. Skipping undefined keys keeps existing cached values intact.
 *
 * @param params - URL params to merge (practiceId, appointmentId, qrMode)
 */
export function storeAllParams(params: Partial<KioskMachineContext>): void {
  const currentContext = getMachineContext();

  // Merge only the defined params so undefined companion fields don't clobber
  // (and thereby erase) existing cached values.
  const definedParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  ) as Partial<KioskMachineContext>;

  const updatedContext: KioskMachineContext = {
    ...currentContext,
    ...definedParams,
  };

  saveMachineContext(updatedContext);
}

/**
 * Retrieve machine context from localStorage
 *
 * @returns Machine context with practiceId, appointmentId, qrMode, etc.
 */
export function retrieveAllParams(): KioskMachineContext {
  return getMachineContext();
}

/**
 * Whether a kiosk launch carries any persistable URL param.
 *
 * Gates the store-and-dissolve reconciliation in the provider. `ehrLocationId`
 * is included: a corrective launch that supplies only `?ehrLocationId=` must
 * still store and dissolve it (self-healing a stuck tablet), the same way a
 * `?practiceId=`-only launch does. Omitting it would leave the kiosk on a stale
 * cached location or treat it as unconfigured on the next bare launch.
 */
export function hasKioskUrlParams(params: {
  practiceId?: string | null;
  appointmentId?: string | null;
  qrMode?: string | null;
  ehrLocationId?: string | null;
  terminalId?: string | null;
  kioskName?: string | null;
}): boolean {
  return Boolean(
    params.practiceId ||
      params.appointmentId ||
      params.qrMode ||
      params.ehrLocationId ||
      params.terminalId ||
      params.kioskName,
  );
}

/**
 * Resolve a kiosk config id, treating the URL value as authoritative.
 *
 * On a fresh kiosk launch the start URL carries the correctly-provisioned id
 * (e.g. practiceId / ehrLocationId). A stale cached value in localStorage must
 * never override it — that stale-cache-beats-correct-URL precedence is what
 * misrouted check-ins to the wrong Experity PM board. When the URL no longer
 * carries the id (in-session navigation, after it has been dissolved from the
 * URL), fall back to the cached value.
 *
 * Returns `undefined` when neither source supplies a value, so callers can fail
 * safe instead of defaulting to a real practice.
 *
 * @param urlValue - Value read from the URL query param (authoritative)
 * @param cachedValue - Value previously persisted to localStorage
 */
export function resolveUrlAuthoritativeId(
  urlValue: string | null | undefined,
  cachedValue: string | null | undefined,
): string | undefined {
  return urlValue || cachedValue || undefined;
}

/**
 * Update specific fields in machine context
 *
 * @param updates - Partial context to merge
 */
export function updateMachineContext(
  updates: Partial<KioskMachineContext>,
): void {
  const currentContext = getMachineContext();
  const updatedContext = {
    ...currentContext,
    ...updates,
  };
  saveMachineContext(updatedContext);
}

/**
 * Get a specific field from machine context
 *
 * @param field - Field name to retrieve
 * @returns Field value or undefined
 */
export function getContextField<K extends keyof KioskMachineContext>(
  field: K,
): KioskMachineContext[K] | undefined {
  const context = getMachineContext();
  return context[field];
}
